import React, { useState } from 'react';
import { User, Star, X } from 'lucide-react';
import { useClients } from '../../../contexts/ClientsContext';

const ClientInput = ({ onClientChange, onCancel }) => {
  const { clients, addClient, updateClient } = useClients();
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setShowResults(true);
  };

  const handleSelectClient = (client) => {
    setSearchTerm(client.name);
    setShowResults(false);
    setSelectedClient(client);
    onClientChange(client);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowResults(true);
  };

  const handleCreateClient = async (withCurrentAccount = false) => {
    if (!searchTerm.trim()) return;

    const existingClient = clients.find(c => c.name.toLowerCase() === searchTerm.toLowerCase());
    if (existingClient) {
      handleSelectClient(existingClient);
      return;
    }

    try {
      const newClient = await addClient({
        name: searchTerm.trim(),
        hasCurrentAccount: withCurrentAccount,
        balance: 0
      });
      handleSelectClient(newClient);
    } catch (err) {
      console.error('Error al crear cliente:', err);
    }
  };

  const handleToggleCurrentAccount = async () => {
    if (!searchTerm.trim()) return;

    const existingClient = selectedClient || clients.find(c => c.name.toLowerCase() === searchTerm.toLowerCase());
    
    try {
      if (existingClient) {
        if (existingClient.balance < 0) return;

        const updatedClient = await updateClient(existingClient.id, {
          ...existingClient,
          hasCurrentAccount: !existingClient.hasCurrentAccount
        });
        handleSelectClient(updatedClient);
      } else {
        await handleCreateClient(true);
      }
    } catch (err) {
      console.error('Error al actualizar cuenta corriente:', err);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <User className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => setShowResults(true)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            placeholder="Buscar o crear cliente..."
          />
        </div>
        <button
          onClick={handleToggleCurrentAccount}
          disabled={selectedClient?.hasCurrentAccount}
          className={`p-2 rounded-lg transition-colors ${
            (selectedClient || clients.find(c => c.name === searchTerm))?.hasCurrentAccount 
              ? 'text-yellow-500' 
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <Star className="w-6 h-6" />
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {showResults && searchTerm && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setShowResults(false)}
          />
          <div className="absolute top-full left-0 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border mt-2 z-20">
            {filteredClients.length > 0 ? (
              filteredClients.map(client => (
                <button
                  key={client.id}
                  className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
                  onClick={() => handleSelectClient(client)}
                >
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{client.name}</div>
                    {client.hasCurrentAccount && (
                      <div className={`text-sm ${
                        client.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        Saldo: ${client.balance.toLocaleString()}
                      </div>
                    )}
                  </div>
                  {client.hasCurrentAccount && (
                    <Star className={`w-4 h-4 ${
                      client.balance < 0 ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'
                    }`} />
                  )}
                </button>
              ))
            ) : (
              <button
                onClick={() => handleCreateClient(false)}
                className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-blue-600 dark:text-blue-400"
              >
                Crear "{searchTerm}"
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ClientInput; 